import { getData, getName } from "country-list";
import {
  beautifyDurationUnit,
  calculateFinancialSupport,
  toCurrency,
} from "../../../helpers";
import { EducationType } from "../../school/constants/types";
import {
  ProgramAccommodationFee,
  programDetailsConfig,
  PROGRAM_TYPES,
} from "../constants";
import {
  beautifyRequiredDocuments,
  calcFeeWithScholarshipAndFinancialAid,
  sumTuitionAndOtherFeesSupports,
} from "./helperFunctions";
import {
  IAccommodationFee,
  IFee,
  IProgramViewMoreBackEndModel,
  IProgramViewMoreFrontEndModel,
} from "../types";

export const parseToViewMoreFrontEndModel = (
  program: IProgramViewMoreBackEndModel,
) => {
  const schoolImage = program.academic.school.image?.preview;
  const schoolCountry =
    program.academic.school.country && getName(program.academic.school.country);
  const requiredDocs = beautifyRequiredDocuments(program.academic.requiredDocs);
  const programType = PROGRAM_TYPES.find(
    (type) => type.id === program.programType,
  )!.name;
  const branches = program.branches.map((branch) => branch.name);
  const countries = getData();
  const restrictedCountries = program.restrictedCountries?.map(
    (country) => countries.find((c) => c.code === country)!.name,
  );

  const programDuration = `${program.durationAmount} ${beautifyDurationUnit(
    program.durationAmount,
    program.durationUnit,
    "Program",
  )}`;

  const tuitionDuration = `${
    program.tuitionDurationAmount == null
      ? ""
      : program.tuitionDurationAmount + " "
  }
        ${beautifyDurationUnit(
          program.tuitionDurationAmount,
          program.tuitionDurationUnit,
          "Program",
        )}`;

  const details = program.details.map((detail) => ({
    name: programDetailsConfig.find((d) => d.type === detail.type)!.name,
    content: detail.content,
    type: detail.type,
  }));

  const {
    feeWithCurrency: tuitionWithCurrency,
    calcFee: calcTuition,
    calcFeeWithCurrency: calcTuitionWithCurrency,
    scholarshipWithCurrency: tuitionScholarshipWithCurrency,
    financialAidWithCurrency: tuitionFinancialAidWithCurrency,
    hasSupport: hasTuitionSupport,
  } = calcFeeWithScholarshipAndFinancialAid(
    program.tuition,
    program.hasTuitionScholarship,
    program.tuitionScholarshipType,
    program.tuitionScholarshipAmount,
    program.hasTuitionFinancialAid,
    program.tuitionFinancialAidType,
    program.tuitionFinancialAidAmount,
    program.currency,
  );

  const {
    feeWithCurrency: otherFeesWithCurrency,
    calcFee: calcOtherFees,
    calcFeeWithCurrency: calcOtherFeesWithCurrency,
    scholarshipWithCurrency: otherFeesScholarshipWithCurrency,
    financialAidWithCurrency: otherFeesFinancialAidWithCurrency,
    hasSupport: hasOtherFeesSupport,
  } = calcFeeWithScholarshipAndFinancialAid(
    program.otherFeesTotal,
    program.hasOtherFeesScholarship,
    program.otherFeesScholarshipType,
    program.otherFeesScholarshipAmount,
    program.hasOtherFeesFinancialAid,
    program.otherFeesFinancialAidType,
    program.otherFeesFinancialAidAmount,
    program.currency,
  );

  const {
    tuitionOtherFeesSum,
    tuitionOtherFeesWithSupportsSum,
    hasFeesSupport,
  } = sumTuitionAndOtherFeesSupports(
    program.tuition,
    hasTuitionSupport,
    calcTuition,
    program.otherFeesTotal,
    hasOtherFeesSupport,
    calcOtherFees,
    program.currency,
  );

  const commission = calculateFinancialSupport(
    program.tuition,
    true,
    program.commissionType,
    program.commissionAmount,
  );

  const otherFees = program.otherFees.map((fee) => ({
    name: fee.name,
    amount: toCurrency(fee.amount, program.currency, " ", "code"),
  }));

  const accommodationFees: IAccommodationFee[] = [];

  if (program.academic.school.typeId === EducationType.Secondary) {
    program.boardingAmount !== null &&
      accommodationFees.push({
        name: ProgramAccommodationFee.ROOM_AND_BOARD,
        label: "Room and Board",
        amount: program.boardingAmount!,
        amountWithCurrency: toCurrency(
          program.boardingAmount,
          program.currency,
          " ",
          "code",
        ),
      });

    program.homestayAmount !== null &&
      accommodationFees.push({
        name: ProgramAccommodationFee.HOMESTAY,
        label: "Homestay",
        amount: program.homestayAmount!,
        amountWithCurrency: toCurrency(
          program.homestayAmount,
          program.currency,
          " ",
          "code",
        ),
      });

    program.insuranceAmount !== null &&
      accommodationFees.push({
        name: ProgramAccommodationFee.INSURANCE,
        label: "Insurance",
        amount: program.insuranceAmount!,
        amountWithCurrency: toCurrency(
          program.insuranceAmount,
          program.currency,
          " ",
          "code",
        ),
      });
  }

  const scholarships: IFee[] = [
    {
      name: "Tuition Fee",
      amount: tuitionScholarshipWithCurrency,
    },
    {
      name: "Other Fees",
      amount: otherFeesScholarshipWithCurrency,
    },
  ];

  const financialAids: IFee[] = [
    {
      name: "Tuition Fee",
      amount: tuitionFinancialAidWithCurrency,
    },
    {
      name: "Other Fees",
      amount: otherFeesFinancialAidWithCurrency,
    },
  ];

  const parsedProgram: IProgramViewMoreFrontEndModel = {
    restrictedCountries,
    requiredDocs,
    programType,
    branches,
    details,
    school: {
      website: program.academic.school.website,
      typeId: program.academic.school.typeId,
      phone: program.academic.school.phone,
      email: program.academic.school.email,
      name: program.academic.school.name,
      country: schoolCountry!,
      image: schoolImage,
    },
    fees: {
      supports: [
        {
          name: "Scholarship",
          support: scholarships,
        },
        {
          name: "Financial Aid",
          support: financialAids,
        },
      ],
      commission: toCurrency(commission, program.currency, " ", "code"),
      accommodationFees: {
        fees: accommodationFees,
        hasFees: program.academic.school.typeId === EducationType.Secondary,
        zeroFee: toCurrency(0, program.currency, " ", "code"),
      },
      otherFees: {
        otherFeesTotalWithSupport: calcOtherFeesWithCurrency,
        otherFeesTotal: otherFeesWithCurrency,
        hasSupport: hasOtherFeesSupport,
        fees: otherFees,
      },
      total: {
        totalWithSupport: tuitionOtherFeesWithSupportsSum,
        total: tuitionOtherFeesSum,
        hasSupport: hasFeesSupport,
      },
      mainFee: {
        feeWithSupport: calcTuitionWithCurrency,
        hasSupport: hasTuitionSupport,
        fee: tuitionWithCurrency,
        name: "Tuition",
      },
      currencyId: program.currency,
      duration: tuitionDuration,
    },
    isMasked: program.isMasked,
    housingTypeNames: program.housingTypeNames?.join(", "),
    schoolTypeName: program.academic.schoolTypeName,
    degreeName: program.academic.degree?.name,
    levelName: program.academic.levelName,
    extraDocuments: program.detailFiles,
    intakes: program.intakesGrouped,
    grade: program.academic.grade,
    majorName: program.majorName,
    duration: programDuration,
    exams: program.exams,
  };

  return parsedProgram;
};
